import type { RendererOverrides, SectionSet } from "@tento-ui/brand/page-engine.types";
import { resolveRenderer } from "@tento-ui/brand/renderer-resolution";
import type { PageModel } from "@tento-ui/brand/page-model";
import { PageModel as PageModelParser } from "@tento-ui/brand/page-model";
import { DomUtils } from "@tento-ui/dom";

import type { PageEngineContext, PageEngineNodeProps } from "./to-props";
import { toProps } from "./to-props";
import type { DecoratorPlugin, FlowAnchor } from "./page-decorators";
import { PageDecorators } from "./page-decorators";

export class PageEngine {
  constructor(
    private page: PageModel,
    private sectionSet: SectionSet<PageEngineNodeProps>,
    private overrides: Option<RendererOverrides<PageEngineNodeProps>>,
    private context: PageEngineContext,
  ) {}

  render(route: Option<HTMLElement>): HTMLElement {
    const $root = this.rootElement(route);
    const $sections: HTMLElement[] = [];

    for (const node of this.page.nodes) {
      const renderer = resolveRenderer(node, this.sectionSet.sections, this.overrides);
      if (!renderer) continue;

      const props = toProps(this.page, node, this.context);
      const $existing = this.findExistingNode($root, node.id);
      const signature = nodeSignature(node);
      const $section = $existing?.dataset.pageNodeSignature === signature
        ? $existing
        : $existing
        ? renderer.bind($existing, props)
        : renderer.make(props);
      $section.dataset.pageNodeId = node.id;
      $section.dataset.pageNodeSignature = signature;
      $sections.push($section);
    }

    // Reserve in-flow decorator slots before committing so reconcile keeps them.
    const reservations = this.reserveFlowSlots($root, $sections);

    DomUtils.reconcileChildren($root, $sections);

    this.runDecorators($root, reservations);
    return $root;
  }

  // Splice a keyed, reusable slot into the section list at each applicable flow
  // decorator's anchor. The slot survives reconcile by identity; the body fills
  // it after reconcile (runDecorators).
  private reserveFlowSlots(
    $root: HTMLElement,
    $sections: HTMLElement[],
  ): PageEngine.FlowReservation[] {
    const reservations: PageEngine.FlowReservation[] = [];
    for (const plugin of PageDecorators.applicable(this.page)) {
      if (plugin.placement.kind !== "flow") continue;
      const index = this.anchorIndex($sections, plugin.placement.anchor);
      if (index == null) continue;
      const $slot = this.ensureFlowSlot($root, plugin);
      $sections.splice(index, 0, $slot);
      reservations.push({ plugin, $slot });
    }
    return reservations;
  }

  private anchorIndex(
    $sections: HTMLElement[],
    anchor: FlowAnchor,
  ): Option<number> {
    const kind = "before" in anchor ? anchor.before : anchor.after;
    const node = this.page.nodes.find((candidate) => candidate.kind === kind);
    if (!node) return null;
    const index = $sections.findIndex(
      ($section) => $section.dataset.pageNodeId === node.id,
    );
    if (index < 0) return null;
    return "before" in anchor ? index : index + 1;
  }

  private ensureFlowSlot(
    $root: HTMLElement,
    plugin: DecoratorPlugin,
  ): HTMLElement {
    const existing = this.findExistingSlot($root, plugin.id);
    if (existing) return existing;

    const $slot = document.createElement("div");
    $slot.dataset.pageSlotId = plugin.id;
    if (plugin.placement.kind === "flow" && plugin.placement.reserve) {
      $slot.style.minHeight = `${plugin.placement.reserve.minHeight}px`;
    }
    return $slot;
  }

  private findExistingSlot(
    $root: HTMLElement,
    slotId: string,
  ): Option<HTMLElement> {
    const candidates = $root.querySelectorAll<HTMLElement>("[data-page-slot-id]");
    for (const $candidate of candidates) {
      if ($candidate.dataset.pageSlotId === slotId) return $candidate;
    }
    return null;
  }

  private runDecorators(
    $root: HTMLElement,
    reservations: PageEngine.FlowReservation[],
  ): void {
    for (const reservation of reservations) {
      void this.fillFlowSlot(reservation);
    }
    for (const plugin of PageDecorators.applicable(this.page)) {
      if (plugin.placement.kind === "overlay") {
        void this.runOverlay($root, plugin);
      }
    }
  }

  // Attach-once: a re-render that reuses the slot does not re-fire attach.
  private async fillFlowSlot(
    reservation: PageEngine.FlowReservation,
  ): Promise<void> {
    const { plugin, $slot } = reservation;
    if ($slot.dataset.pageSlotFilled === "true") return;
    $slot.dataset.pageSlotFilled = "true";
    const body = await PageDecorators.load(plugin);
    await body.attach($slot, this.page);
  }

  private async runOverlay(
    $root: HTMLElement,
    plugin: DecoratorPlugin,
  ): Promise<void> {
    const body = await PageDecorators.load(plugin);
    await body.attach($root, this.page);
  }

  private rootElement(route: Option<HTMLElement>): HTMLElement {
    if (route?.hasAttribute("data-page-engine-root")) {
      return route;
    }

    const $root = document.createElement("div");
    $root.dataset.pageEngineRoot = "true";
    return $root;
  }

  private findExistingNode(
    $root: HTMLElement,
    nodeId: string,
  ): Option<HTMLElement> {
    const candidates = $root.querySelectorAll<HTMLElement>("[data-page-node-id]");
    for (const $candidate of candidates) {
      if ($candidate.dataset.pageNodeId === nodeId) return $candidate;
    }
    return null;
  }
}

export namespace PageEngine {
  export type FlowReservation = {
    plugin: DecoratorPlugin;
    $slot: HTMLElement;
  };
}

function nodeSignature(node: PageModel["nodes"][number]): string {
  return JSON.stringify({
    kind: node.kind,
    props: node.props,
    children: node.children,
  });
}

export function extractPageModel(route: Option<HTMLElement>): Option<PageModel> {
  const $script = route?.querySelector<HTMLScriptElement>(
    'script[type="application/json"][data-page-model]',
  );
  const text = $script?.textContent;
  if (!text) return null;

  try {
    return PageModelParser.parse(JSON.parse(text));
  } catch {
    return null;
  }
}
